import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Facebook, ChevronDown } from 'lucide-react';
import kuzniaLogo from '@/assets/kil_logo.webp';

type NavLink = {
  label: string;
  href: string;
  route?: boolean;
  children?: { label: string; href: string; route?: boolean }[];
};

const NAV_LINKS: NavLink[] = [
  { label: 'O nas', href: '#o-nas' },
  { label: 'Aktualności', href: '/aktualnosci', route: true },
  { label: 'Działaj Lokalnie', href: '#dzialaj-lokalnie' },
  { label: 'Galeria', href: '#galeria' },
  {
    label: 'Dokumenty',
    href: '#dokumenty',
    children: [{ label: 'Dokumenty DL 2026', href: '#dzialaj-lokalnie?tab=dokumenty' }],
  },
  { label: 'Samorządy', href: '#samorzady' },
  { label: 'Partnerzy', href: '#partnerzy' },
  { label: 'Kontakt', href: '#kontakt' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Observe on-page sections to highlight the current one in the nav.
  useEffect(() => {
    if (!isHome) {
      setActiveSection('');
      return;
    }
    const ids = NAV_LINKS
      .filter((l) => !l.route && l.href.startsWith('#'))
      .map((l) => l.href.slice(1));
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (elements.length === 0) return;

    const visibility = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibility.set(entry.target.id, entry.intersectionRatio);
        }
        let bestId = '';
        let bestRatio = 0;
        for (const [id, ratio] of visibility) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        }
        if (bestRatio > 0) setActiveSection(bestId);
      },
      { rootMargin: '-96px 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isHome, location.pathname]);

  const handleAnchorClick = (e: React.MouseEvent, href: string) => {
    setIsOpen(false);
    if (!href.startsWith('#')) return;
    // If the hash contains a query param (e.g. #dzialaj-lokalnie?tab=dokumenty),
    // set it directly so the browser fires hashchange and the section can parse it.
    if (href.includes('?')) {
      e.preventDefault();
      window.location.hash = href;
      return;
    }
    if (isHome) {
      // default browser anchor behavior is fine; ensure smooth scroll
      e.preventDefault();
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', href);
      }
    } else {
      e.preventDefault();
      navigate('/' + href); // e.g. /#kontakt → Index reads hash
    }
  };

  const isActive = (link: Pick<NavLink, 'href' | 'route'>) => {
    if (link.route) return location.pathname.startsWith(link.href);
    if (!isHome) return false;
    const href = link.href;
    if (href.startsWith('#')) {
      const hashId = href.slice(1).split('?')[0];
      return activeSection === hashId;
    }
    return link.href === `#${activeSection}`;
  };

  const linkClasses = (active: boolean, mobile = false) => {
    const base = mobile
      ? 'text-sm font-medium py-2 rounded px-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background'
      : 'text-sm font-medium rounded px-2 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background';
    const state = active
      ? mobile
        ? 'bg-primary/10 text-primary font-semibold border-l-4 border-primary pl-3'
        : 'text-primary font-semibold'
      : 'text-foreground/80 hover:text-primary';
    return `${base} ${state}`;
  };

  const handleChildClick = (e: React.MouseEvent, href: string) => {
    setIsOpen(false);
    if (href.startsWith('/#')) {
      e.preventDefault();
      navigate(href);
    } else if (href.startsWith('#')) {
      handleAnchorClick(e, href);
    }
  };

  const renderChildLink = (child: NavLink['children'][number], mobile = false) => {
    const active = isActive(child);
    const cls = linkClasses(active, mobile);
    const ariaCurrent = active ? ('page' as const) : undefined;
    const key = child.route ? child.href : child.href;
    if (child.route || child.href.startsWith('/#')) {
      return (
        <Link
          key={key}
          to={child.href}
          onClick={() => setIsOpen(false)}
          className={cls}
          aria-current={ariaCurrent}
        >
          {child.label}
        </Link>
      );
    }
    return (
      <a
        key={key}
        href={child.href}
        onClick={(e) => handleChildClick(e, child.href)}
        className={cls}
        aria-current={ariaCurrent}
      >
        {child.label}
      </a>
    );
  };

  const renderLink = (link: NavLink, mobile = false) => {
    const active = isActive(link);
    const cls = linkClasses(active, mobile);
    const ariaCurrent = active ? ('page' as const) : undefined;
    if (link.children) {
      if (mobile) {
        return (
          <div key={link.href} className="flex flex-col gap-1">
            <span className={cls} aria-current={ariaCurrent}>
              {link.label}
            </span>
            <div className="pl-4 flex flex-col gap-1 border-l border-border">
              {link.children.map((child) => renderChildLink(child, true))}
            </div>
          </div>
        );
      }
      return (
        <div key={link.href} className="relative group">
          <a
            href={link.href}
            onClick={(e) => handleAnchorClick(e, link.href)}
            className={`${cls} inline-flex items-center gap-1`}
            aria-haspopup="true"
            aria-expanded="false"
            aria-current={ariaCurrent}
          >
            {link.label}
            <ChevronDown size={14} className="transition-transform group-hover:rotate-180" aria-hidden="true" />
          </a>
          <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200 z-50">
            <div className="bg-background border border-border rounded-lg shadow-lg py-2 px-1 min-w-[12rem] flex flex-col gap-1">
              {link.children.map((child) => renderChildLink(child, false))}
            </div>
          </div>
        </div>
      );
    }
    if (link.route) {
      return (
        <Link key={link.href} to={link.href} onClick={() => setIsOpen(false)} className={cls} aria-current={ariaCurrent}>
          {link.label}
        </Link>
      );
    }
    return (
      <a key={link.href} href={link.href} onClick={(e) => handleAnchorClick(e, link.href)} className={cls} aria-current={ariaCurrent}>
        {link.label}
      </a>
    );
  };


  return (
    <nav aria-label="Główna nawigacja" className={`sticky top-0 z-50 transition-shadow duration-300 bg-background ${scrolled ? 'shadow-md' : ''}`}>
      <div className="container flex items-center justify-between py-3">
        <Link to="/" className="flex items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label="Kuźnia — strona główna">
          <img src={kuzniaLogo} alt="Logo Kuźnia Inicjatyw Lokalnych" width={160} height={40} className="h-10 w-auto" />
          <span className="font-heading font-bold text-lg hidden sm:inline">Kuźnia</span>
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map((link) => renderLink(link, false))}
          <a
            href="https://facebook.com/kuzniainicjatywlokalnychwlisewie"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook Kuźnia (otwiera się w nowej karcie)"
            className="text-foreground/80 hover:text-primary transition-colors rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Facebook size={20} />
          </a>
        </div>

        <button className="lg:hidden p-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" onClick={() => setIsOpen(!isOpen)} aria-label="Menu" aria-expanded={isOpen} aria-controls="mobile-menu">
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {isOpen && (
        <div id="mobile-menu" className="lg:hidden border-t bg-background animate-fade-in-up">
          <div className="container py-4 flex flex-col gap-3">
            {NAV_LINKS.map((link) => renderLink(link, true))}
            <a
              href="https://facebook.com/kuzniainicjatywlokalnychwlisewie"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook Kuźnia (otwiera się w nowej karcie)"
              onClick={() => setIsOpen(false)}
              className="text-sm font-medium py-2 text-foreground/80 hover:text-primary transition-colors rounded px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary flex items-center gap-2"
            >
              <Facebook size={18} /> Facebook
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
